/**
 * Deep freeze an object.
 *
 * Useful for freezing fixture objects for identifying flaky tests that are flaky due to test interference.
 *
 * @example
 * ```
 * // Test file 1
 * it("does something cool", () => {
 *  const x = PLATE_CREATION_TABLE_NOTE_PART;
 *  x.item.name = 'A new name'
 * })
 *
 * // Test File 2
 * it("does something cool too", () => {
 *   const y = PLATE_CREATION_TABLE_NOTE_PART;
 *   expect(y.item.name).equal("Plate creation 1");
 * })
 * ```
 *
 * If test 1 and 2 are run in the same shard in that order, then it's likely Test 2 will fail, but it would
 * be difficult to understand why.
 * Wrapping the fixture with `deepFreezeObject` would cause Test 1 to fail,
 * pinpointing the root cause of the flake. It would additionally introduce type errors in tests that are
 * modifying the fixture.
 *
 * Just redefine the fixture to be:
 *
 * `export const PLATE_CREATION_TABLE_NOTE_PART = deepFreezeObject<NotePart>({...})`
 * N.B. This utility requires that a type is provided.
 */
export default function deepFreezeObject<T>(o: NoInfer<T>): NoInfer<T> {
  if (typeof o !== 'object' || o == null) {
    return o;
  }
  if (Array.isArray(o)) {
    return Object.freeze(o.map((elem) => deepFreezeObject(elem))) as T;
  }
  const frozen: {[key: string]: unknown} = {};
  for (const [key, value] of Object.entries(o)) {
    frozen[key] = deepFreezeObject(value);
  }
  return Object.freeze<T>(frozen as T);
}
